/**
 * CGCUserAdminPage — iter-255 (Feb 2026)
 * ======================================
 * Full-page (NOT modal / drawer) admin console for a single CloudGM
 * customer account. Replaces the previous `CGCUserAdminDrawer` popup
 * that was hiding behind the app header/footer and truncating content.
 *
 * Route: /manage-platform/cgc-users/:userId
 * Query: ?email=<email>         # required when the local `userId` does
 *                               # NOT match the customer platform's id
 *                               # (Sign-in-with-Apple relay emails, etc.)
 *
 * Behaviour:
 *   · Skeleton loaders while data hydrates
 *   · Breadcrumb + prominent Back button (browser back-safe)
 *   · Immediate global cache revalidation via SWR-style `fetchOnFocus`
 *     on every state-mutating action
 *   · When the customer platform returns 404, we render a helpful
 *     "message the Customer Platform Agent" panel with a pre-filled
 *     copy-to-clipboard payload — no more silent failure.
 *   · Inline edit-mode toggle for profile fields (currently a no-op
 *     wire, backend endpoint pending — see /admin/cgc-users/:id PATCH).
 *   · RBAC: guarded by ProtectedRoute at the App.js layer.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  Loader2, ShieldCheck, ShieldOff, Ban,
  BadgeCheck, XCircle, Mail, Phone, Calendar, AlertTriangle,
  User as UserIcon, Link2, Key, Award, Hash, ArrowLeft, ArrowRight,
  Copy, Send, RefreshCw, Edit3, Save, X,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import DashboardHeader from '../../components/DashboardHeader';
import BreadcrumbNav from '../../components/BreadcrumbNav';
import { useLanguage } from '../../context/LanguageContext';
import { getAuthHeaders } from '../../utils/authHeaders';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CGCUserAdminPage() {
  const { userId } = useParams();
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const t = useCallback((ar, en) => (isAr ? ar : en), [isAr]);

  const emailFromQuery = (sp.get('email') || '').trim();

  const [user, setUser] = useState(null);
  const [archived, setArchived] = useState(false);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [notFoundDetail, setNotFoundDetail] = useState('');
  const [localFallback, setLocalFallback] = useState(false); // iter-260 — CGC 404 but local record exists
  const [busy, setBusy] = useState('');

  // iter-269 — showBlockForm / blockReason removed since block modal
  // is not on this page anymore (moved to the outer accounts list).

  const loadUser = useCallback(async () => {
    setLoading(true);
    setNotFound(false);
    setNotFoundDetail('');
    setLocalFallback(false);
    // iter-253: email → by-email lookup wins because CGC platform IDs
    //   differ from our local ids (Apple relay emails, especially).
    const attempts = [];
    if (emailFromQuery) attempts.push(`/admin/cgc-users/by-email/${encodeURIComponent(emailFromQuery)}`);
    if (userId)          attempts.push(`/admin/cgc-users/${userId}`);

    let lastErr = null;
    for (const path of attempts) {
      try {
        const r = await axios.get(`${API}${path}`, { headers: getAuthHeaders() });
        setUser(r.data || null);
        // iter-267.1 — proxy now emits `_local_only:true` when it fell
        // back to the local CGC snapshot. Show the sky-blue banner.
        if (r.data?._local_only) setLocalFallback(true);
        // iter-259 — check local archive membership
        try {
          const arch = await axios.get(`${API}/admin/cgc-users/archived`, { headers: getAuthHeaders() });
          const rows = arch.data?.data || [];
          setArchived(rows.some((row) => row.user_id === (r.data?.id || userId)));
        } catch (_e) { setArchived(false); }
        setLoading(false);
        return;
      } catch (err) {
        lastErr = err;
      }
    }
    // iter-260 — CGC upstream returned 404 but we may still have a local
    // snapshot in `db_customers.users` (e.g. old record before the
    // customer platform re-migrated ids, or an Apple-relay account we
    // can only see locally). Try `/api/admin/cloudgm-users/{id}` so the
    // admin never hits a dead-end screen — they get identity + activity
    // + ban/archive controls scoped to the LOCAL record.
    if (userId) {
      try {
        const r2 = await axios.get(`${API}/admin/cloudgm-users/${userId}`, { headers: getAuthHeaders() });
        if (r2.data && r2.data.id) {
          const local = r2.data;
          // Reshape local doc into the same shape CGCUserAdminPage expects.
          setUser({
            id: local.id,
            email: local.email || emailFromQuery,
            name: local.name || local.full_name || `${local.first_name || ''} ${local.last_name || ''}`.trim(),
            first_name: local.first_name || '',
            last_name: local.last_name || '',
            phone: local.phone || '',
            phone_verified: false,
            email_verified: false,
            account_verified: false,
            platform: 'cloudgm (local)',
            role: local.role || 'customer',
            membership_number: local.membership_number || '',
            membership_tier: local.membership_tier || '',
            loyalty_points: local.loyalty_points || 0,
            loyalty_tier: local.loyalty_tier || '',
            created_at: local.created_at,
            last_login_at: local.last_login_at,
            has_password: false,
            linked_providers: { google: { linked: false }, apple: { linked: false } },
            is_banned: local.status === 'banned' || !!local.is_banned,
            ban_reason: local.ban_reason || '',
            banned_at: local.banned_at || null,
            _local_only: true,          // signal for the UI
            _activity: local.activity || {},
          });
          setLocalFallback(true);
          setLoading(false);
          return;
        }
      } catch (_e) { /* fall through — truly missing */ }
    }
    setUser(null);
    setNotFound(true);
    setNotFoundDetail(lastErr?.response?.data?.detail || lastErr?.message || 'unknown');
    setLoading(false);
  }, [userId, emailFromQuery]);

  useEffect(() => { loadUser(); }, [loadUser]);

  // iter-259 — Archive / Restore actions
  // iter-269 — All account state mutations (block/unblock/archive/restore
  // /delete) live outside this page. This admin page is now read-only for
  // account state; the only mutation it still owns is force-unlink of
  // Google/Apple providers (per-account).

  // ─── Actions ─────────────────────────────────────────────────────
  const forceUnlink = async (provider) => {
    const linkedProviders = user?.linked_providers || {};
    const other = provider === 'google' ? 'apple' : 'google';
    const otherLinked = !!linkedProviders[other]?.linked;
    if (!user.has_password && !otherLinked) {
      if (!window.confirm(t(
        `⚠️ العميل بلا كلمة مرور ولا مزود ${other === 'google' ? 'جوجل' : 'آبل'} مرتبط. فك ربط ${provider} سيقفل الحساب. متابعة؟`,
        `⚠️ Customer has no password and no ${other} link. Force-unlinking ${provider} will lock them out. Continue?`,
      ))) return;
    } else if (!window.confirm(t(
      `فك ربط ${provider} من ${user.email}؟`,
      `Force-unlink ${provider} from ${user.email}?`,
    ))) return;

    setBusy(`unlink-${provider}`);
    try {
      const r = await axios.delete(`${API}/admin/cgc-users/${user.id}/linked-providers/${provider}`,
                                    { headers: getAuthHeaders() });
      if (r.data?.changed === false) toast.info(t('لا يوجد ربط لفكه', 'Provider not linked'));
      else                            toast.success(t('تم فك الربط', 'Unlinked'));
      await loadUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally { setBusy(''); }
  };

  // iter-269 — Block / unblock actions removed. They live on the outer
  // accounts list only. This page just shows the read-only state.

  // Breadcrumb — CloudGMUsersTab lives at /manage/cloudgm?tab=cloudgm-users
  const breadcrumb = useMemo(() => [
    { label_ar: 'لوحة التحكم', label_en: 'Dashboard', path: '/dashboard' },
    { label_ar: 'CLOUDGM FZ LLC', label_en: 'CLOUDGM FZ LLC', path: '/manage/cloudgm' },
    { label_ar: 'عملاء CloudGM المسجلون', label_en: 'CloudGM Customers', path: '/manage/cloudgm?tab=cloudgm-users' },
    { label_ar: 'إدارة الحساب', label_en: 'Manage Account', path: null },
  ], []);

  const backToList = () => navigate('/manage/cloudgm?tab=cloudgm-users');

  return (
    <div className="min-h-screen bg-[#030303] text-white" dir={isAr ? 'rtl' : 'ltr'}>
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-cyan-500/5 rounded-full blur-[150px]" />
      </div>

      <DashboardHeader onBack={backToList} />
      <div className="border-b border-white/5 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <BreadcrumbNav items={breadcrumb} />
        </div>
      </div>

      <main className="relative z-10 py-6 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header row */}
          <div className="flex items-start justify-between gap-3 mb-6 flex-wrap">
            <div className="flex items-center gap-3">
              <Button
                onClick={backToList}
                variant="outline"
                size="sm"
                className="border-white/10 text-white/70 hover:bg-white/5"
                data-testid="cgc-page-back"
              >
                {isAr ? <ArrowRight className="h-4 w-4 me-2" /> : <ArrowLeft className="h-4 w-4 me-2" />}
                {t('رجوع للقائمة', 'Back to list')}
              </Button>
              <div>
                <h1 className="text-xl md:text-2xl font-bold text-white flex items-center gap-2" data-testid="cgc-page-title">
                  <UserIcon className="h-5 w-5 text-cyan-400" />
                  {t('إدارة حساب العميل', 'Manage Customer Account')}
                </h1>
                <p className="text-[11px] text-white/40 mt-0.5" dir="ltr">
                  {user?.email || emailFromQuery || '—'}
                </p>
              </div>
            </div>
            <Button onClick={loadUser} variant="outline" size="sm"
                    className="border-white/10 text-white/70 hover:bg-white/5" data-testid="cgc-page-refresh">
              <RefreshCw className="h-4 w-4 me-2" />
              {t('تحديث', 'Refresh')}
            </Button>
          </div>

          {loading ? (
            <SkeletonState />
          ) : notFound ? (
            <NotFoundState email={emailFromQuery || userId} detail={notFoundDetail} t={t} isAr={isAr} onRetry={loadUser} />
          ) : user ? (
            <UserBody
              user={user}
              archived={archived}
              localFallback={localFallback}
              t={t}
              isAr={isAr}
              busy={busy}
              forceUnlink={forceUnlink}
            />
          ) : null}
        </div>
      </main>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────── */

function SkeletonState() {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          transition={{ delay: i * 0.05 }}
          className="rounded-2xl bg-white/[0.02] border border-white/10 p-5 space-y-3"
        >
          <div className="h-3 w-24 rounded bg-white/10 animate-pulse" />
          <div className="h-3 w-full rounded bg-white/10 animate-pulse" />
          <div className="h-3 w-3/4 rounded bg-white/10 animate-pulse" />
          <div className="h-3 w-1/2 rounded bg-white/10 animate-pulse" />
        </motion.div>
      ))}
    </div>
  );
}

/* ── NotFoundState ────────────────────────────────────────────────── */
function NotFoundState({ email, detail, t, isAr, onRetry }) {
  const [copied, setCopied] = useState(false);

  const agentMessage = `مرحباً وكيل منصة العملاء 👋

نحاول فتح بطاقة "إدارة الحساب" من لوحة تحكم ASB606 للعميل التالي، لكن الـ API الخاص بكم يُرجع 404 — الحساب غير موجود عند البحث بالبريد.

📧 البريد: ${email || '(missing)'}
🔗 Endpoints تم تجربتها:
   • GET /api/admin/customer-users/by-email/{email}
   • GET /api/admin/customer-users/{id}

نتيجة السيرفر:
"${detail}"

ملاحظات مهمة:
1) البريد قد يكون من Apple Sign-in (privaterelay.appleid.com). لو كنتم تخزّنون البريد الحقيقي بدل الـ relay email، احتاج نتفاهم على مفتاح البحث.
2) قد يكون البحث case-sensitive. تأكدوا من الـ .toLowerCase() على الطرفين.
3) لو الحساب مسجل بـ Apple فقط بلا رقم جوال، أرجع لنا الـ user_id + is_apple_only بحيث ألتقطه من عندنا.

المطلوب منكم:
✅ تأكيد أن الحساب موجود فعلاً على قاعدة بياناتكم بهذا الإيميل (أو الـ relay email).
✅ تفعيل lookup by-email حساس-غير-حساس على الأحرف.
✅ إرجاع الحقول: id, email, first_name, last_name, phone, has_password, linked_providers{google,apple}, is_banned, ban_reason.

بمجرد ما يشتغل الطرف عندكم، تلقائياً بيشتغل عندي بدون تغيير كود على ASB606.

شكراً 🙏`;

  const copy = async () => {
    // iter-255 fix — some browsers/headless envs deny clipboard permission;
    // fall back to document.execCommand('copy') via a hidden textarea so the
    // button never fails silently.
    let ok = false;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(agentMessage);
        ok = true;
      }
    } catch (_e) { /* fall through to legacy path */ }
    if (!ok) {
      try {
        const ta = document.createElement('textarea');
        ta.value = agentMessage;
        ta.style.position = 'fixed'; ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        ok = document.execCommand('copy');
        document.body.removeChild(ta);
      } catch (_e) { /* still failed */ }
    }
    if (ok) {
      setCopied(true);
      toast.success(t('تم النسخ — الصقها في محادثة الوكيل', 'Copied — paste it in the agent chat'));
      setTimeout(() => setCopied(false), 3000);
    } else {
      toast.error(t('تعذر النسخ — اختر النص يدوياً وانسخه', 'Copy failed — select the text manually'));
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl border border-red-500/30 bg-red-500/[0.03] p-6"
      data-testid="cgc-page-not-found"
    >
      <div className="flex items-start gap-4 flex-wrap">
        <div className="h-12 w-12 rounded-full bg-red-500/10 border border-red-500/40 flex items-center justify-center shrink-0">
          <AlertTriangle className="h-6 w-6 text-red-400" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-lg font-bold text-white mb-1">
            {t('لم نتمكن من إيجاد هذا العميل على منصة CGC', 'Customer not found on the CGC platform')}
          </h2>
          <p className="text-[13px] text-white/60 mb-4 leading-relaxed">
            {t(
              'الحساب غير مسجل بعد، أو أن الـ API الخاص بمنصة العملاء لا يدعم البحث بهذا البريد الإلكتروني (Apple Private Relay مثلاً). أرسل الرسالة الجاهزة أدناه لوكيل منصة العملاء وسنعالج المشكلة معاً.',
              'The account is either not yet registered on the platform, or the customer-platform API does not support lookup by this email (Apple Private Relay is a common cause). Send the ready-made message below to the Customer Platform Agent.',
            )}
          </p>
          <div className="text-[11px] text-white/40 mb-4 font-mono" dir="ltr">
            <p>Email tried: <span className="text-white/70">{email || '(none)'}</span></p>
            <p>Server said: <span className="text-red-300">"{detail || 'unknown'}"</span></p>
          </div>

          <div className="rounded-xl border border-white/10 bg-black/40 overflow-hidden">
            <div className="px-3 py-2 border-b border-white/5 flex items-center justify-between">
              <p className="text-[11px] text-white/50 font-semibold">
                {t('رسالة جاهزة لوكيل منصة العملاء', 'Ready-made message to Customer Platform Agent')}
              </p>
              <div className="flex items-center gap-1">
                <Button size="sm" variant="outline" onClick={copy}
                        className={`h-7 px-2 border-white/10 text-[11px] ${copied ? 'text-emerald-300 border-emerald-500/40 bg-emerald-500/10' : 'text-white/70'}`}
                        data-testid="cgc-page-copy-agent-msg">
                  {copied ? <BadgeCheck className="h-3 w-3 me-1" /> : <Copy className="h-3 w-3 me-1" />}
                  {copied ? t('تم النسخ', 'Copied') : t('نسخ', 'Copy')}
                </Button>
              </div>
            </div>
            <pre className="p-3 text-[11px] text-white/70 whitespace-pre-wrap leading-relaxed max-h-64 overflow-y-auto"
                 dir={isAr ? 'rtl' : 'ltr'}>
{agentMessage}
            </pre>
          </div>

          <div className="flex items-center gap-2 mt-4 flex-wrap">
            <Button onClick={onRetry} variant="outline" size="sm"
                    className="border-white/10 text-white/70">
              <RefreshCw className="h-3 w-3 me-1" />
              {t('إعادة المحاولة', 'Retry')}
            </Button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

/* ── UserBody ─────────────────────────────────────────────────────── */
function UserBody({
  user, archived, localFallback, t, isAr, busy,
  forceUnlink,
}) {
  const isBlocked = !!user.is_banned;

  // Simple stats derived from the payload
  const stats = [
    { label: t('عدد المزودين المرتبطين', 'Linked providers'),
      value: (user.linked_providers?.google?.linked ? 1 : 0) + (user.linked_providers?.apple?.linked ? 1 : 0),
      accent: 'cyan' },
    { label: t('كلمة مرور', 'Password'),
      value: user.has_password ? t('مفعّلة', 'Set') : t('غير مفعّلة', 'Not set'),
      accent: user.has_password ? 'emerald' : 'gray' },
    { label: t('النقاط', 'Points'),
      value: user.loyalty_points ?? 0,
      accent: 'amber' },
    { label: t('حالة الحساب', 'Status'),
      value: archived ? t('مؤرشف', 'Archived') : isBlocked ? t('محظور', 'Blocked') : t('فعّال', 'Active'),
      accent: archived ? 'amber' : isBlocked ? 'red' : 'emerald' },
  ];

  return (
    <div className="space-y-5">
      {/* iter-260 — Local-only fallback banner: CGC platform has no record
          for this account but our local `cloudgm_customers.users` collection
          does. We still show identity + activity + local admin controls. */}
      {localFallback && (
        <div className="rounded-xl border border-sky-500/30 bg-sky-500/[0.06] p-4 flex items-start gap-3"
             data-testid="cgc-page-local-fallback-banner">
          <AlertTriangle className="h-4 w-4 text-sky-300 mt-0.5" />
          <div className="flex-1 text-[12px] text-sky-100 leading-relaxed">
            <p className="font-semibold mb-1">
              {t('هذا الحساب موجود محلياً فقط', 'This account exists only locally')}
            </p>
            <p className="text-white/70">
              {t(
                'هذا الحساب موجود في نسخة snapshot المحلية فقط ولم نجده على منصة العملاء (CGC). فك الربط والحظر والأرشفة والحذف تعمل على السجل المحلي، وسنُعاد المزامنة تلقائياً عند إنشاء الحساب على CGC بنفس البريد.',
                'This account only exists in our local snapshot and was not found on the CGC platform. Unlink, block, archive and delete work on the local record and will re-sync automatically when the same email appears on CGC.',
              )}
            </p>
          </div>
        </div>
      )}

      {/* iter-259 — Archived banner (read-only after iter-269) */}
      {archived && (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/[0.06] p-4 flex items-center gap-3"
             data-testid="cgc-page-archived-banner">
          <ShieldOff className="h-4 w-4 text-amber-300" />
          <div className="flex-1 text-[12px] text-amber-200">
            {t(
              'هذا الحساب في الأرشيف. لاستعادته توجّه إلى صفحة الأرشيف.',
              'This account is in the archive. Restore it from the archive page.',
            )}
          </div>
        </div>
      )}

      {/* Stats row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {stats.map((s, i) => (
          <StatTile key={i} label={s.label} value={s.value} accent={s.accent} />
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* A. Identity — wide */}
        <IdentityCard user={user} t={t} />

        {/* B. Verification */}
        <VerificationCard user={user} t={t} />

        {/* C. Sign-in methods */}
        <SignInMethodsCard user={user} t={t} isAr={isAr} busy={busy} forceUnlink={forceUnlink} />
      </div>

      {/* D. Block status — READ-ONLY (iter-269) */}
      <BlockCard user={user} isBlocked={isBlocked} archived={archived} t={t} />

      {/* Danger zone (cascade delete) removed — iter-267.
          Delete now lives exclusively on the archive page so the
          operator has to move through block → archive first. */}
    </div>
  );
}

/* ── Sub-cards ───────────────────────────────────────────────────── */

function StatTile({ label, value, accent = 'cyan' }) {
  const palette = {
    cyan:    'from-cyan-500/10 border-cyan-500/25 text-cyan-300',
    emerald: 'from-emerald-500/10 border-emerald-500/25 text-emerald-300',
    amber:   'from-amber-500/10 border-amber-500/25 text-amber-300',
    red:     'from-red-500/10 border-red-500/25 text-red-300',
    gray:    'from-white/[0.03] border-white/10 text-white/60',
  }[accent];
  return (
    <div className={`bg-gradient-to-br ${palette} to-white/[0.02] border rounded-xl p-3`}>
      <p className="text-[11px] text-white/50 mb-1">{label}</p>
      <p className="text-lg font-bold text-white">{value}</p>
    </div>
  );
}

function IdentityCard({ user, t }) {
  return (
    <section className="bg-white/[0.02] border border-white/10 rounded-2xl p-5 lg:col-span-2"
             data-testid="cgc-page-identity">
      <h3 className="text-[11px] uppercase text-white/40 mb-4 flex items-center gap-2">
        <UserIcon className="h-3 w-3" /> {t('الهوية', 'Identity')}
      </h3>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-[12px]">
        <Field label={t('الاسم', 'Name')}
               value={user.name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || '—'} />
        <Field label={t('البريد', 'Email')} value={user.email} dir="ltr" />
        <Field label={t('الهاتف', 'Phone')} value={user.phone} dir="ltr" />
        <Field label={t('رقم العضوية', 'Membership')} value={user.membership_number} />
        <Field label={t('الفئة', 'Tier')} value={`${user.membership_tier || '—'} · ${user.loyalty_tier || ''}`} />
        <Field label={t('المنصة', 'Platform')} value={user.platform} />
        <Field label={t('الدور', 'Role')} value={user.role} />
        <Field label={t('تاريخ الإنشاء', 'Created')} value={fmtDate(user.created_at)} />
        <Field label={t('آخر دخول', 'Last login')} value={fmtDate(user.last_login_at)} />
      </div>
    </section>
  );
}

function VerificationCard({ user, t }) {
  return (
    <section className="bg-white/[0.02] border border-white/10 rounded-2xl p-5"
             data-testid="cgc-page-verification">
      <h3 className="text-[11px] uppercase text-white/40 mb-4">{t('التحقق', 'Verification')}</h3>
      <div className="flex flex-col gap-2">
        <VerifyRow ok={user.email_verified} label={t('البريد الإلكتروني', 'Email verified')} icon={Mail} />
        <VerifyRow ok={user.phone_verified} label={t('رقم الهاتف', 'Phone verified')} icon={Phone} />
        <VerifyRow ok={user.account_verified} label={t('الحساب معتمد', 'Account verified')} icon={BadgeCheck} />
      </div>
    </section>
  );
}

function SignInMethodsCard({ user, t, isAr, busy, forceUnlink }) {
  return (
    <section className="bg-white/[0.02] border border-white/10 rounded-2xl p-5 lg:col-span-3"
             data-testid="cgc-page-signin-methods">
      <h3 className="text-[11px] uppercase text-white/40 mb-4">{t('طرق تسجيل الدخول', 'Sign-in Methods')}</h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-[12px]
          ${user.has_password ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-black/30 border-white/5'}`}
          data-testid="cgc-page-signin-password">
          <Key className="h-3 w-3" style={{ color: user.has_password ? '#34d399' : '#71717a' }} />
          <span className="font-semibold text-white">{t('البريد + كلمة مرور', 'Email + Password')}</span>
          <span className="text-white/60 ms-auto">
            {user.has_password ? (isAr ? 'مفعّل' : 'enabled') : (isAr ? 'معطّل' : 'disabled')}
          </span>
        </div>
        <ProviderRow brand="Google" brandColor="#EA4335"
          data={user.linked_providers?.google}
          onUnlink={() => forceUnlink('google')}
          busy={busy === 'unlink-google'} isAr={isAr} testIdKey="google" />
        <ProviderRow brand="Apple" brandColor="#ffffff"
          data={user.linked_providers?.apple}
          onUnlink={() => forceUnlink('apple')}
          busy={busy === 'unlink-apple'} isAr={isAr} testIdKey="apple" />
      </div>
    </section>
  );
}

// iter-269 — BlockCard is now READ-ONLY.
// Block/unblock/archive/restore are performed exclusively from the outer
// accounts list and the archive page. This card just surfaces the
// account's current state (blocked/active + reason + archive flag).
function BlockCard({ user, isBlocked, archived, t }) {
  return (
    <section className={`rounded-2xl p-5 border ${isBlocked
      ? 'bg-red-500/5 border-red-500/30'
      : 'bg-emerald-500/5 border-emerald-500/20'}`}
      data-testid="cgc-page-block-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className={`text-sm font-semibold flex items-center gap-2 ${isBlocked ? 'text-red-300' : 'text-emerald-300'}`}>
            {isBlocked ? <Ban className="h-4 w-4" /> : <ShieldCheck className="h-4 w-4" />}
            {isBlocked ? t('الحساب محظور', 'Account is blocked') : t('الحساب فعّال', 'Account is active')}
            {archived && (
              <span className="ms-2 text-[10px] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/30">
                {t('في الأرشيف', 'In archive')}
              </span>
            )}
          </h3>
          {isBlocked && user.ban_reason && (
            <p className="text-[12px] text-red-200 mt-1">
              {t('السبب: ', 'Reason: ')}"{user.ban_reason}"
            </p>
          )}
          {isBlocked && user.banned_at && (
            <p className="text-[10px] text-white/40 mt-0.5">
              {t('محظور منذ ', 'Since ')}{fmtDate(user.banned_at)}
            </p>
          )}
          <p className="text-[10px] text-white/40 mt-2 leading-relaxed">
            {t(
              'الحظر والأرشفة يتمّان من صفحة الحسابات الخارجية. الحذف من صفحة الأرشيف فقط.',
              'Block / archive actions live on the outer accounts list. Delete lives on the archive page only.',
            )}
          </p>
        </div>
      </div>
    </section>
  );
}

/* iter-267 — DangerZone (cascade delete) removed entirely from the
   admin page. Delete lives exclusively on the archive page so the
   operator must move through block → archive first. */

/* ── Primitives ─────────────────────────────────────────────────── */

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return iso; }
};

const Field = ({ label, value, dir }) => (
  <div>
    <p className="text-[10px] text-white/40 mb-0.5">{label}</p>
    <p className="text-white/85 truncate text-[13px]" dir={dir || undefined}>{value || '—'}</p>
  </div>
);

const VerifyRow = ({ ok, label, icon: Icon }) => (
  <div className={`flex items-center justify-between gap-2 rounded-lg border px-3 py-2 text-[12px]
    ${ok ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-white/[0.02] border-white/5'}`}>
    <div className="flex items-center gap-2">
      <Icon className={`h-3 w-3 ${ok ? 'text-emerald-400' : 'text-white/40'}`} />
      <span className="text-white/85">{label}</span>
    </div>
    {ok
      ? <BadgeCheck className="h-3.5 w-3.5 text-emerald-400" />
      : <XCircle className="h-3.5 w-3.5 text-white/30" />}
  </div>
);

const ProviderRow = ({ brand, brandColor, data, onUnlink, busy, isAr, testIdKey }) => {
  const linked = !!(data && data.linked);
  const linkedDate = data?.linked_at
    ? (() => {
        try { return new Date(data.linked_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); }
        catch { return ''; }
      })()
    : '';
  return (
    <div className={`rounded-lg border px-3 py-2 text-[12px] flex flex-col gap-1
      ${linked ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-black/30 border-white/5'}`}
      data-testid={`cgc-page-provider-${testIdKey}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Link2 className="h-3 w-3" style={{ color: linked ? '#34d399' : '#71717a' }} />
          <span className="font-semibold" style={{ color: brandColor }}>{brand}</span>
          <span className="text-white/60 truncate" dir="ltr">
            {linked
              ? `${data.provider_email || (isAr ? '(بريد مخفي)' : '(hidden)')}`
              : (isAr ? 'غير مرتبط' : 'not linked')}
          </span>
        </div>
        {linked && (
          <button onClick={onUnlink} disabled={busy}
                  className="h-7 w-7 rounded-md bg-red-500/10 hover:bg-red-500/20 text-red-400 flex items-center justify-center disabled:opacity-50 shrink-0"
                  data-testid={`cgc-page-unlink-${testIdKey}`}
                  title={isAr ? `فك ربط ${brand}` : `Force-unlink ${brand}`}>
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <ShieldOff className="h-3 w-3" />}
          </button>
        )}
      </div>
      {linked && (linkedDate || data?.provider_uid) && (
        <div className="flex items-center gap-3 pl-5 text-[10px] text-white/40">
          {linkedDate && (
            <span>{isAr ? 'مربوط منذ' : 'Linked'} {linkedDate}</span>
          )}
          {data?.provider_uid && (
            <span className="font-mono truncate" dir="ltr" title={data.provider_uid}>
              uid: {data.provider_uid}
            </span>
          )}
        </div>
      )}
    </div>
  );
};
